import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { livePhotoUrl, canSeeGT } from "@/lib/fmUtils";
import { useAuth } from "@/contexts/AuthContext";

const LAYOUT = "main products";

const fmtPrice = (v) => {
  const n = Number(v);
  return v === "" || v == null || Number.isNaN(n) ? "-" : n.toFixed(2);
};
const fmtQty = (v) => {
  const n = Number(v);
  return v === "" || v == null || Number.isNaN(n) || n === 0 ? "-" : Math.round(n);
};

export default function BestSellingPrint() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showGt = canSeeGT(user);
  const [printed, setPrinted] = useState(false);
  const autoPrintTimer = useRef(null);

  const layout = useMemo(() => {
    const v = sessionStorage.getItem("bestSellingPrintLayout");
    return v === "1x1" ? "1x1" : "1x2";
  }, []);
  const isOnePerPage = layout === "1x1";

  useEffect(() => {
    if (user && !showGt) navigate("/", { replace: true });
  }, [user, showGt, navigate]);

  const records = useMemo(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem("bestSellingPrintRecords") || "[]");
      // Print ordering: category then sub_category (blanks last)
      const key = (rec) => {
        const fd = rec.fieldData || {};
        const cat = (fd.category || "").trim().toLowerCase();
        const sub = (fd.sub_category || "").trim().toLowerCase();
        return [cat === "" ? 1 : 0, cat, sub === "" ? 1 : 0, sub];
      };
      return raw.sort((a, b) => {
        const ka = key(a);
        const kb = key(b);
        for (let i = 0; i < ka.length; i++) {
          if (ka[i] < kb[i]) return -1;
          if (ka[i] > kb[i]) return 1;
        }
        return 0;
      });
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!records.length || printed) return;
    autoPrintTimer.current = setTimeout(() => {
      setPrinted(true);
      window.print();
    }, 2500);
    return () => clearTimeout(autoPrintTimer.current);
  }, [records, printed]);

  if (!records.length) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4" data-testid="print-empty-state">
        <p className="text-sm text-zinc-500">No products selected for printing.</p>
        <Button variant="outline" className="rounded-sm border-zinc-300" onClick={() => navigate("/")} data-testid="print-back-button">
          <ArrowLeft size={14} className="mr-1.5" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl bg-white p-6" data-testid="best-selling-print-page">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .bs-1x1-block {
            height: 48vh;
            overflow: hidden;
            break-after: page;
            page-break-after: always;
          }
          .bs-1x1-block:last-child {
            break-after: auto;
            page-break-after: auto;
          }
        }
      `}</style>
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Button variant="outline" size="sm" className="rounded-sm border-zinc-300" onClick={() => navigate("/")} data-testid="print-back-button">
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <Button size="sm" className="rounded-sm bg-blue-600 text-white hover:bg-blue-700" onClick={() => window.print()} data-testid="print-now-button">
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>

      <h1 className="mb-4 text-lg font-bold text-zinc-900">
        Best Selling Items ({records.length}) — Layout {layout}
      </h1>

      {records.map((rec) => {
        const fd = rec.fieldData || {};
        const variants = rec.portalData?.product || [];
        return (
          <div
            key={rec.recordId}
            className={`mb-6 border border-zinc-300 p-4 ${isOnePerPage ? "bs-1x1-block" : ""}`}
            style={{ breakInside: "avoid" }}
            data-testid={`print-product-block-${rec.recordId}`}
          >
            <div className="flex gap-4">
              <img
                src={livePhotoUrl(LAYOUT, rec.recordId, "photo")}
                alt="product"
                className="h-[300px] w-[300px] shrink-0 border border-zinc-200 object-contain"
                onError={(e) => {
                  e.target.style.visibility = "hidden";
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-zinc-900">{fd["item no"] || ""}</div>
                <div className="mt-1 text-sm text-zinc-700">{fd.description || "-"}</div>
                {fd.selling_history_date && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Distribution Date: <span className="font-medium text-zinc-800">{fd.selling_history_date}</span>
                  </div>
                )}
                {fd.selling_history && (
                  <img
                    src={livePhotoUrl(LAYOUT, rec.recordId, "selling_history")}
                    alt="selling history"
                    className="mt-2 w-full border border-zinc-200 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
              </div>
            </div>

            {variants.length > 0 && (
              <table className="mt-3 w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-zinc-400 text-left text-[10px] uppercase tracking-[0.05em] text-zinc-500">
                    <th className="py-1 pr-2">Customer Item No.</th>
                    {showGt && <th className="py-1 pr-2">GT SKU</th>}
                    <th className="py-1 pr-2">Size</th>
                    <th className="py-1 pr-2 text-right">Pcs/CTN</th>
                    <th className="py-1 pr-2 text-right">Last PO Price</th>
                    <th className="py-1 text-right">Update Price</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr key={v.recordId} className="border-b border-zinc-200 text-zinc-800">
                      <td className="py-1 pr-2">{v.customer_item_no || "-"}</td>
                      {showGt && <td className="py-1 pr-2">{v["product::GT_SKU"] || "-"}</td>}
                      <td className="py-1 pr-2">{v["product::Size"] || "-"}</td>
                      <td className="py-1 pr-2 text-right">{fmtQty(v["product::装箱率"])}</td>
                      <td className="py-1 pr-2 text-right">{fmtPrice(v["product::GT_Price"])}</td>
                      <td className="py-1 text-right">{fmtPrice(v["product::Price"])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
